import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import * as CognitoAuthService from "../../services/CognitoAuthService";

/*
 * Two-step Cognito forgot-password flow, ported from formik + yup to
 * react-hook-form + zod:
 *   1. user enters email → CognitoAuthService.forgotPassword sends a
 *      6-digit code (rendered by our customMessage Lambda).
 *   2. user enters code + new password → CognitoAuthService.confirmPassword
 *      finishes the reset; redirect to /login.
 */

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();

  const [step, setStep] = useState<1 | 2>(1);
  const [requestedEmail, setRequestedEmail] = useState("");

  return (
    <>
      <Helmet>
        <title>{t("forgot-password.title")} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        {step === 1 ? (
          <RequestStep
            onSent={(email) => {
              setRequestedEmail(email);
              setStep(2);
              showNotification("success", t("forgot-password.email-sent"));
            }}
            onError={(message) => showNotification("error", message)}
          />
        ) : (
          <ConfirmStep
            email={requestedEmail}
            onReset={() => {
              showNotification(
                "success",
                t("forgot-password.reset-success", "Password reset. You can now sign in.")
              );
              navigate("/login");
            }}
            onError={(message) => showNotification("error", message)}
            onUseDifferentEmail={() => setStep(1)}
          />
        )}
      </main>
    </>
  );
}

// ---------- Step 1: request the code ----------------------------------

interface RequestStepProps {
  onSent: (email: string) => void;
  onError: (message: string) => void;
}

function RequestStep({ onSent, onError }: RequestStepProps) {
  const { t } = useTranslation();

  const schema = z.object({
    email: z
      .string()
      .min(1, t("forgot-password.required-email"))
      .email(t("forgot-password.invalid-email")),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: Values) {
    try {
      await CognitoAuthService.forgotPassword(values.email);
      onSent(values.email);
    } catch (err) {
      const message =
        (err as { message?: string })?.message ||
        t("forgot-password.email-not-sent");
      onError(String(message));
    }
  }

  return (
    <Form {...form}>
      <form
        autoComplete="off"
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <h1 className="text-2xl font-bold tracking-tight">
          {t("forgot-password.title")}
        </h1>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("forgot-password.email-label")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="username"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col items-center gap-3">
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {t("forgot-password.send-button")}
          </Button>
          <Button asChild variant="link" size="sm">
            <RouterLink to="/login">
              {t("forgot-password.back-to-login", "Back to login")}
            </RouterLink>
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ---------- Step 2: confirm code + new password -----------------------

interface ConfirmStepProps {
  email: string;
  onReset: () => void;
  onError: (message: string) => void;
  onUseDifferentEmail: () => void;
}

function ConfirmStep({
  email,
  onReset,
  onError,
  onUseDifferentEmail,
}: ConfirmStepProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const schema = z
    .object({
      code: z
        .string()
        .trim()
        .regex(
          /^\d{6}$/,
          t("forgot-password.invalid-code", "Code must be 6 digits")
        ),
      password: z
        .string()
        .min(
          10,
          t(
            "forgot-password.password-too-short",
            "Password must be at least 10 characters"
          )
        )
        .regex(
          /[A-Z]/,
          t(
            "forgot-password.password-missing-uppercase",
            "Must contain at least one uppercase letter"
          )
        )
        .regex(
          /[a-z]/,
          t(
            "forgot-password.password-missing-lowercase",
            "Must contain at least one lowercase letter"
          )
        )
        .regex(
          /[0-9]/,
          t(
            "forgot-password.password-missing-number",
            "Must contain at least one number"
          )
        )
        .regex(
          /[^A-Za-z0-9]/,
          t(
            "forgot-password.password-missing-symbol",
            "Must contain at least one special character"
          )
        ),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t(
        "forgot-password.passwords-must-match",
        "Passwords must match"
      ),
    });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { code: "", password: "", confirmPassword: "" },
  });

  async function onSubmit(values: Values) {
    try {
      await CognitoAuthService.confirmPassword(
        email,
        values.code.trim(),
        values.password
      );
      onReset();
    } catch (err) {
      const message =
        (err as { message?: string })?.message ||
        t("forgot-password.reset-failed", "Could not reset password");
      onError(String(message));
    }
  }

  return (
    <Form {...form}>
      <form
        autoComplete="off"
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {t(
              "forgot-password.confirm-title",
              "Enter the verification code"
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "forgot-password.confirm-description",
              "A 6-digit code was sent to {{email}}. Enter it together with the new password.",
              { email }
            )}
          </p>
        </div>

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("forgot-password.code-label", "Verification code")}
              </FormLabel>
              <FormControl>
                <Input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  className="text-center text-2xl tracking-[0.5rem]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("forgot-password.new-password-label", "New password")}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  show={showPassword}
                  onToggleShow={() => setShowPassword((s) => !s)}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(
                  "forgot-password.password-requirements",
                  "Must be at least 12 characters with uppercase, lowercase, number, and symbol"
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  "forgot-password.confirm-password-label",
                  "Confirm new password"
                )}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  show={showPassword}
                  onToggleShow={() => setShowPassword((s) => !s)}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col items-center gap-3">
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {t("forgot-password.reset-button", "Reset password")}
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              onUseDifferentEmail();
              form.reset();
            }}
          >
            {t(
              "forgot-password.use-different-email",
              "Use a different email"
            )}
          </Button>
          <Button asChild variant="link" size="sm">
            <RouterLink to="/login">
              {t("forgot-password.back-to-login", "Back to login")}
            </RouterLink>
          </Button>
        </div>
      </form>
    </Form>
  );
}

